// src/cockpit4/components/KnowledgeEvolutionPanel.tsx
// ============================================
// Knowledge Evolution Panel - 知识演化面板
//
// 依据白皮书第十五章《Knowledge Evolution Protocol》：
//   - 展示 30_Inbox/ 候选知识（全自动收集）
//   - 三档处理：低风险自动通过 / 中风险待确认 / 高风险必须 CEO 确认
//   - CEO 可执行「确认入库」/「拒绝归档」
//   - 展示"今天公司学到了什么"日报
//   - 可手动触发 run_daily_evolution
//
// 数据来源（禁止假数据）：
//   RuntimeManager.query(KnowledgeEvolution, list_candidates / get_stats / list_reports)
//   RuntimeManager.execute(KnowledgeEvolution, run_daily_evolution / confirm_candidate / reject_candidate)
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrainCircuit,
  RefreshCw,
  Loader2,
  Check,
  X,
  FileText,
  Database,
  Inbox,
  Play,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import {
  KnowledgeEvolutionQuery,
  KnowledgeEvolutionCommand,
  KnowledgeSourceType,
  CANDIDATE_STATUS_LABEL,
  RISK_LEVEL_LABEL,
  KNOWLEDGE_SOURCE_LABEL,
} from "../../runtime/KnowledgeEvolutionRuntime";

// ============================================
// 审计日志（P2：对齐 P1-8 审计活动流）
// 知识演化操作（运行/确认入库/拒绝归档）统一写入 xeaos_audit_logs
// 失败静默：审计写入不阻塞主流程
// ============================================

const AUDIT_STORAGE_KEY = "xeaos_audit_logs";

function appendAudit(opts: {
  action: string;
  module: string;
  target: string;
  detail: string;
  severity: "info" | "warning" | "critical";
}) {
  try {
    const logs = JSON.parse(localStorage.getItem(AUDIT_STORAGE_KEY) || "[]");
    logs.push({
      id: `audit-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      timestamp: new Date().toISOString(),
      userId: "system",
      username: "Hermes",
      action: opts.action,
      module: opts.module,
      target: opts.target,
      detail: opts.detail,
      isSensitive: opts.severity === "critical",
      severity: opts.severity,
    });
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(logs));
  } catch {
    // 静默
  }
}

// ============================================
// 类型
// ============================================

interface CandidateKnowledge {
  id: string;
  title: string;
  summary: string;
  content: string;
  source: KnowledgeSourceType;
  sourceRef: string;
  riskLevel: "low" | "medium" | "high";
  status: string;
  detectedAt: string;
  targetCategory: string;
  score: number;
  feedback?: string;
}

interface DailyReport {
  date: string;
  generatedAt: string;
  newCandidates: number;
  autoApproved: number;
  needsReview: number;
  needsCeo: number;
  insights: Array<{ title: string; summary: string; source: KnowledgeSourceType }>;
  vaultPath: string;
}

interface Stats {
  totalCandidates: number;
  byStatus?: Record<string, number>;
  byRisk?: Record<string, number>;
  bySource?: Record<string, number>;
  lastRunAt?: string;
  lastReport?: DailyReport;
}

const RISK_COLOR: Record<string, string> = {
  low: "#34D399",
  medium: "#FBBF24",
  high: "#F87171",
};

const SOURCE_ICON: Record<string, string> = {
  hermes_dialogue: "💬",
  runtime_log: "🖥️",
  review_feedback: "📝",
  git_commit: "🔧",
  customer_follow_up: "🤝",
};

// ============================================
// 主组件
// ============================================

export default function KnowledgeEvolutionPanel() {
  const [candidates, setCandidates] = useState<CandidateKnowledge[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  // 双层解包 Runtime 响应（RuntimeManager 包一层，KnowledgeEvolutionRuntime 再包一层）
  const unwrap = (resp: any): any => RuntimeManager.unwrapResponse(resp);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [candResp, statsResp, repResp] = await Promise.all([
        RuntimeManager.query(RuntimeName.KnowledgeEvolution, {
          type: KnowledgeEvolutionQuery.ListCandidates,
        } as any),
        RuntimeManager.query(RuntimeName.KnowledgeEvolution, {
          type: KnowledgeEvolutionQuery.GetStats,
        } as any),
        RuntimeManager.query(RuntimeName.KnowledgeEvolution, {
          type: KnowledgeEvolutionQuery.ListReports,
        } as any),
      ]);
      const candData = unwrap(candResp);
      setCandidates(candData?.candidates ?? []);
      setStats(unwrap(statsResp) as Stats | null);
      // 兼容 reports 是数组或 { reports: [...] } 结构
      const repData = unwrap(repResp);
      setReports(
        Array.isArray(repData) ? (repData as DailyReport[]) : (repData?.reports as DailyReport[]) ?? []
      );
    } catch (err: any) {
      setToast({ type: "error", text: `加载失败：${err?.message || String(err)}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunEvolution = async () => {
    setRunning(true);
    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.KnowledgeEvolution,
        KnowledgeEvolutionCommand.RunDailyEvolution,
        {}
      );
      const data = unwrap(resp);
      const msg = resp?.success
        ? `✅ 知识演化完成：新增 ${data?.extracted ?? 0} 条候选知识，日报已生成`
        : `❌ 知识演化失败：${resp?.error || "未知错误"}`;
      // P2：写审计活动流（对齐 P1-8）
      if (resp?.success) {
        appendAudit({
          action: "run_daily_evolution",
          module: "knowledge_evolution",
          target: "知识演化",
          detail: `运行知识演化：新增 ${data?.extracted ?? 0} 条候选知识，日报已生成`,
          severity: "info",
        });
      }
      setToast({ type: resp?.success ? "ok" : "error", text: msg });
      await load();
    } catch (err: any) {
      setToast({ type: "error", text: `运行失败：${err?.message || String(err)}` });
    } finally {
      setRunning(false);
    }
  };

  const handleConfirm = async (id: string) => {
    const resp = await RuntimeManager.execute(
      RuntimeName.KnowledgeEvolution,
      KnowledgeEvolutionCommand.ConfirmCandidate,
      { id }
    );
    setToast({
      type: resp?.success ? "ok" : "error",
      text: resp?.success ? `✅ 候选知识已确认入库：${id}` : `❌ ${resp?.error || "失败"}`,
    });
    // P2：写审计活动流（对齐 P1-8）
    if (resp?.success) {
      appendAudit({
        action: "confirm_candidate",
        module: "knowledge_evolution",
        target: id,
        detail: `确认候选知识入库：${id}`,
        severity: "info",
      });
    }
    await load();
  };

  const handleReject = async (id: string) => {
    const resp = await RuntimeManager.execute(
      RuntimeName.KnowledgeEvolution,
      KnowledgeEvolutionCommand.RejectCandidate,
      { id, feedback: "CEO 拒绝归档" }
    );
    setToast({
      type: resp?.success ? "ok" : "error",
      text: resp?.success ? `🗑️ 候选知识已拒绝归档：${id}` : `❌ ${resp?.error || "失败"}`,
    });
    // P2：写审计活动流（对齐 P1-8）
    if (resp?.success) {
      appendAudit({
        action: "reject_candidate",
        module: "knowledge_evolution",
        target: id,
        detail: `拒绝候选知识归档：${id}`,
        severity: "warning",
      });
    }
    await load();
  };

  const lastReport = stats?.lastReport ?? reports[0];

  return (
    <div className="space-y-3">
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`px-3 py-2 rounded-lg border text-[11px] font-mono ${
              toast.type === "ok"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            {toast.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 顶部：统计 + 运行按钮 */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          <BrainCircuit size={12} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-300">知识演化 Runtime</span>
          <span className="text-[9px] font-mono text-gray-600 px-1.5 py-0.5 rounded bg-white/[0.04]">
            后台运行 · 每天 23:00
          </span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          <Database size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-300">
            累计 {stats?.totalCandidates ?? 0} 条候选知识
          </span>
        </div>
        <button
          onClick={handleRunEvolution}
          disabled={running}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-wider bg-purple-500/15 border border-purple-500/30 text-purple-300 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
        >
          {running ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
          运行知识演化
        </button>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-mono bg-white/[0.03] border border-white/[0.06] text-gray-400 hover:text-gray-200 transition-colors"
        >
          <RefreshCw size={11} /> 刷新
        </button>
      </div>

      {/* 三档处理规则 */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "低风险", desc: "自动通过，直接入库正式知识库", color: "#34D399" },
          { label: "中风险", desc: "标记待确认，CEO 可一键确认/拒绝", color: "#FBBF24" },
          { label: "高风险", desc: "必须 CEO 确认，未确认不入库", color: "#F87171" },
        ].map((r) => (
          <div key={r.label} className="px-2.5 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.color }} />
              <span className="text-[10px] font-mono" style={{ color: r.color }}>
                {r.label}
              </span>
            </div>
            <p className="mt-1 text-[9px] font-mono text-gray-500">{r.desc}</p>
          </div>
        ))}
      </div>

      {/* 最近日报 */}
      {lastReport && (
        <div className="px-3 py-2.5 rounded-lg bg-gradient-to-r from-purple-500/[0.06] to-cyan-500/[0.06] border border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <FileText size={11} className="text-purple-400" />
              <span className="text-[10px] font-mono text-purple-300">
                今天公司学到了什么（{lastReport.date}）
              </span>
            </div>
            <span className="text-[9px] font-mono text-gray-500">
              新增 {lastReport.newCandidates} · 自动入库 {lastReport.autoApproved} · 待确认 {lastReport.needsReview} · 必须确认 {lastReport.needsCeo}
            </span>
          </div>
          {lastReport.insights.length > 0 && (
            <div className="mt-2 space-y-1">
              {lastReport.insights.slice(0, 4).map((ins, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10px] font-mono text-gray-300">
                  <span>{SOURCE_ICON[ins.source] || "📌"}</span>
                  <span className="text-gray-400">{ins.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 候选知识列表 */}
      <div className="rounded-lg border border-white/[0.05] bg-white/[0.01] overflow-hidden">
        <div className="px-3 py-2 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Inbox size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
              候选知识（30_Inbox/）
            </span>
          </div>
          <span className="text-[9px] font-mono text-gray-500">{candidates.length} 条</span>
        </div>
        <div className="max-h-[320px] overflow-y-auto divide-y divide-white/[0.04]">
          {loading ? (
            <div className="p-6 text-center text-[10px] font-mono text-gray-500 flex items-center justify-center gap-2">
              <Loader2 size={12} className="animate-spin" /> 加载中...
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-[11px] font-mono text-gray-500">
                暂无候选知识 · 点击「运行知识演化」扫描 Hermes 对话 / Runtime 日志 / 审核反馈 / 客户跟进记录
              </p>
            </div>
          ) : (
            candidates.map((c) => (
              <div key={c.id} className="px-3 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono text-gray-200 truncate max-w-[220px]">
                    {c.title}
                  </span>
                  <span
                    className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                    style={{ background: `${RISK_COLOR[c.riskLevel]}20`, color: RISK_COLOR[c.riskLevel] }}
                  >
                    {RISK_LEVEL_LABEL[c.riskLevel]}
                  </span>
                  <span className="text-[9px] font-mono text-gray-500">
                    {CANDIDATE_STATUS_LABEL[c.status as keyof typeof CANDIDATE_STATUS_LABEL] || c.status}
                  </span>
                  <span className="text-[9px] font-mono text-gray-600">
                    {SOURCE_ICON[c.source] || "📌"} {KNOWLEDGE_SOURCE_LABEL[c.source] || c.source}
                  </span>
                </div>
                <p className="mt-1 text-[10px] font-mono text-gray-400">{c.summary}</p>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-600">
                    目标分类：{c.targetCategory} · 评分 {c.score}/100
                  </span>
                  {(c.status === "needs_review" || c.status === "needs_ceo" || c.status === "pending") && (
                    <div className="flex items-center gap-1 ml-auto">
                      <button
                        onClick={() => handleConfirm(c.id)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                      >
                        <Check size={9} /> 确认入库
                      </button>
                      <button
                        onClick={() => handleReject(c.id)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-colors"
                      >
                        <X size={9} /> 拒绝归档
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
